import { useRef, useState } from 'react';
import Icon from './Icon.jsx';

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
const MAX_BYTES = 5 * 1024 * 1024;

export const prettySize = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(Math.round(bytes / 1024), 1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** Icon name for a file's MIME type — PDFs read as documents, everything else as images. */
export const fileIcon = (mime) => (mime === 'application/pdf' ? 'file' : 'image');

/**
 * One credential slot: drop a file or browse for it. Validation happens here so
 * the user hears about a 12 MB scan before it goes over the wire — the server
 * enforces the same limits regardless.
 */
export default function DocumentUpload({ kind, label, hint, required, file, existing, onPick, onClear, status, error }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState('');

  const accept = (picked) => {
    setLocalError('');
    if (!picked) return;
    if (!ACCEPT.split(',').includes(picked.type)) {
      return setLocalError('Only PDF, JPG, PNG or WebP files are accepted.');
    }
    if (picked.size > MAX_BYTES) {
      return setLocalError(`That file is ${prettySize(picked.size)} — the limit is 5 MB.`);
    }
    if (picked.size === 0) return setLocalError('That file appears to be empty.');
    onPick(kind, picked);
  };

  const chosen = file || existing;
  const shown = error || localError;

  return (
    <div className={`doc-slot${chosen ? ' filled' : ''}${shown ? ' invalid' : ''}`}>
      <div className="doc-slot-head">
        <span className="doc-slot-label">
          {label}
          {required && (
            <span className="doc-required" title="Required">
              {' '}
              *
            </span>
          )}
        </span>
        <div className="spacer" />
        {status && <span className={`pill pill-${status.tone}`}>{status.text}</span>}
      </div>
      {hint && !chosen && <p className="tiny dim doc-hint">{hint}</p>}

      {chosen ? (
        <div className="doc-chosen">
          <span className="icon-tile icon-tile-neutral" style={{ width: 36, height: 36 }}>
            <Icon name={fileIcon(file ? file.type : existing.mime)} size={18} />
          </span>
          <span className="grow" style={{ minWidth: 0 }}>
            <span className="doc-chosen-name truncate">{file ? file.name : existing.filename}</span>
            <span className="tiny dim">{prettySize(file ? file.size : existing.size)}</span>
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => inputRef.current?.click()}>
            Replace
          </button>
          {onClear && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => onClear(kind)}
              aria-label={`Remove ${label}`}
              style={{ width: 34, height: 34 }}
            >
              <Icon name="trash" size={16} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={`dropzone${dragging ? ' dragging' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            accept(e.dataTransfer.files?.[0]);
          }}
        >
          <span className="icon-tile">
            <Icon name="upload" />
          </span>
          <span className="dropzone-text">
            <strong>Choose a file</strong> or drag it here
          </span>
          <span className="tiny dim">PDF, JPG, PNG or WebP · up to 5 MB</span>
        </button>
      )}

      {shown && (
        <p className="field-error mt-1">
          <Icon name="warning" />
          {shown}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          accept(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
